import {
  isFinalStage,
  trackingSourceName,
  type ShipmentWithScans,
} from "@estelle/domain";
import { Loader2, RefreshCw, TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";

import { can, usePermissions } from "@/auth/permissions";
import { formatDate } from "@/components/table/cells";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ShipmentEscalationLine } from "@/modules/orders/screens/EscalatedLabel";
import { ScanTimeline } from "@/modules/orders/screens/ScanTimeline";
import {
  ExactTime,
  HealthLabel,
  StageBadge,
} from "@/modules/orders/screens/TrackingParts";
import {
  attemptsLabel,
  outOfDateNote,
  waitWords,
} from "@/modules/orders/tracking";
import type { RefreshTracking } from "@/modules/orders/useRefreshTracking";

/**
 * One shipment's tracking, inside its card on the order page (#527, Part B):
 * where the parcel is, whether it needs a look, how many attempts there have
 * been, when it is expected, when it last moved and who said so — then the scan
 * history, newest first.
 *
 * The four empty states say different things and are kept apart: no tracking
 * number at all; a number and no scan yet; tracking given up on after the age
 * cap; and scans none of which the status table knows. Silence on any of them
 * would read as the page being broken.
 */
export function ShipmentTracking({
  shipment,
  refresh,
}: {
  shipment: ShipmentWithScans;
  refresh: RefreshTracking;
}) {
  const hasNumber =
    shipment.trackingNumber !== null && shipment.trackingNumber !== "";

  if (!hasNumber) {
    return (
      <Section>
        <p className="text-muted-foreground text-sm">
          No tracking number yet. Tracking starts as soon as the courier&apos;s
          number reaches Shopify.
        </p>
      </Section>
    );
  }

  const noScans = shipment.stage === null && shipment.scans.length === 0;
  if (noScans) {
    return (
      <Section>
        <p className="text-muted-foreground text-sm">
          The tracking number is on file, but the courier has not reported a
          scan yet.{" "}
          {shipment.trackingCheckedAt ? (
            <>
              Last checked <ExactTime value={shipment.trackingCheckedAt} />.
            </>
          ) : (
            "It has not been checked yet."
          )}
        </p>
        <RefreshRow shipment={shipment} refresh={refresh} />
      </Section>
    );
  }

  const open = !isFinalStage(shipment.stage);
  const source = trackingSourceName(shipment);
  const note = outOfDateNote(shipment);

  return (
    <Section>
      {shipment.openEscalation ? (
        <ShipmentEscalationLine escalation={shipment.openEscalation} />
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        {shipment.stage ? <StageBadge stage={shipment.stage} /> : null}
        <HealthLabel health={shipment.health} />
        {shipment.stage === null ? (
          <span className="text-muted-foreground text-sm">
            Stage not known: none of the courier&apos;s statuses so far is
            recognised.
          </span>
        ) : null}
      </div>

      <dl className="grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
        <Fact label="Delivery attempts">
          {attemptsLabel(shipment.attemptCount)}
        </Fact>
        <Fact label="Expected delivery">
          {shipment.expectedDeliveryDate ? (
            formatDate(shipment.expectedDeliveryDate, false)
          ) : (
            <span className="text-muted-foreground">Not given</span>
          )}
        </Fact>
        <Fact label="Last update">
          {shipment.lastScanAt ? (
            <ExactTime value={shipment.lastScanAt} />
          ) : (
            <span className="text-muted-foreground">No scan yet</span>
          )}
        </Fact>
        {source ? <Fact label="Tracking from">{source}</Fact> : null}
        {shipment.returnTrackingNumber ? (
          <Fact label="Return tracking number">
            <span className="tabular-nums wrap-anywhere">
              {shipment.returnTrackingNumber}
            </span>
          </Fact>
        ) : null}
      </dl>

      {shipment.trackingStoppedAt ? (
        <Notice tone="warning" title="Tracking stopped, needs a look">
          No scan arrived for a long time, so this parcel is no longer checked
          every few hours (stopped{" "}
          {formatDate(shipment.trackingStoppedAt, false)}
          ). Refresh tracking looks at it again.
        </Notice>
      ) : null}

      {note ? (
        <Notice tone="warning" title="Shopify out of date">
          {note} The ERP shows the courier&apos;s status; nothing is sent to
          Shopify.
        </Notice>
      ) : null}

      {open ? <RefreshRow shipment={shipment} refresh={refresh} /> : null}

      {shipment.scans.length > 0 ? (
        <div className="space-y-2 border-t pt-3">
          <h4 className="text-sm font-medium">Scan history</h4>
          <ScanTimeline scans={shipment.scans} />
        </div>
      ) : null}
    </Section>
  );
}

function Section({ children }: { children: ReactNode }) {
  return (
    <section
      aria-label="Tracking"
      className="bg-background space-y-3 rounded-md border p-3"
    >
      {children}
    </section>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 items-baseline justify-between gap-3 sm:block">
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="min-w-0 text-right sm:text-left">{children}</dd>
    </div>
  );
}

function Notice({
  tone,
  title,
  children,
}: {
  tone: "warning";
  title: string;
  children: ReactNode;
}) {
  return (
    <div
      role="note"
      className={cn(
        "flex gap-2 rounded-md border p-2.5 text-sm",
        tone === "warning" &&
          "border-transparent bg-tint-yellow text-foreground",
      )}
    >
      <TriangleAlert
        aria-hidden
        className="mt-0.5 size-4 shrink-0 text-amber-700 dark:text-amber-300"
      />
      <p className="min-w-0">
        <span className="font-medium">{title}.</span> {children}
      </p>
    </div>
  );
}

/**
 * The "Refresh tracking" button and what came of pressing it. Hidden from
 * anyone who cannot open the order, which is the permission the route asks for;
 * the server checks it all the same.
 */
function RefreshRow({
  shipment,
  refresh,
}: {
  shipment: ShipmentWithScans;
  refresh: RefreshTracking;
}) {
  const permissions = usePermissions();
  if (!can(permissions, "orders.detail.view")) return null;

  const mine =
    refresh.result?.shipmentId === shipment.id ? refresh.result.message : null;
  const busy = refresh.pending || refresh.waitSeconds > 0;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="pointer-coarse:h-11 pointer-coarse:px-4"
          disabled={busy}
          aria-busy={refresh.pending}
          onClick={() => {
            refresh.refresh(shipment.id);
          }}
        >
          {refresh.pending ? (
            <Loader2 className="animate-spin" aria-hidden />
          ) : (
            <RefreshCw aria-hidden />
          )}
          {refresh.pending ? "Refreshing…" : "Refresh tracking"}
        </Button>
        {!refresh.pending && refresh.waitSeconds > 0 ? (
          <span className="text-muted-foreground text-xs">
            Available again in {waitWords(refresh.waitSeconds)}
          </span>
        ) : null}
      </div>
      <div aria-live="polite" role="status">
        {mine ? (
          <p
            className={cn(
              "text-sm",
              mine.tone === "problem" && "text-destructive",
              mine.tone === "ok" && "text-emerald-700 dark:text-emerald-300",
              mine.tone === "info" && "text-muted-foreground",
            )}
          >
            {mine.text}
          </p>
        ) : null}
      </div>
    </div>
  );
}
